import {
  Paper,
  TextField,
  Typography,
  useTheme,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import { tokens } from "../../../../../../../theme";
import CustomTextField from "../../../../../../../components/ui/Textfield";
import { useShareTemplates } from "../../../../../../configurations/share-messages/hooks/useShareMessageTemplates";

interface TermsAndConditionsSectionProps {
  onChange?: (value: string) => void;
  initialValue?: string;
  bookingCategory?: string;
}

export default function TermsAndConditionsSection({
  onChange,
  initialValue = "",
  bookingCategory = "",
}: TermsAndConditionsSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [value, setValue] = useState(initialValue);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const { templates, fetchTemplates } = useShareTemplates();

  const safeTemplates = Array.isArray(templates) ? templates : [];
  const termsTemplates = safeTemplates.filter(
    (t) => t.template_type === "terms_conditions",
  );

  useEffect(() => {
    fetchTemplates(bookingCategory, "terms_conditions");
  }, [bookingCategory, fetchTemplates]);

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = termsTemplates.find((t) => t.id === templateId);
    if (template) {
      setValue(template.body_template);
      onChange?.(template.body_template);
    }
  };

  useEffect(() => {
    if (termsTemplates.length > 0 && !selectedTemplateId) {
      const first = termsTemplates[0];
      setSelectedTemplateId(first.id);
      setValue(first.body_template);
      onChange?.(first.body_template);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsTemplates.length]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onChange?.(e.target.value);
  };

  return (
    <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "15px",
          color: colors.primary[100],
          mb: 1.5,
        }}
      >
        Terms & Conditions
      </Typography>

      {termsTemplates.length > 0 ? (
        <CustomTextField
          label="Template"
          fullWidth
          select
          value={selectedTemplateId}
          onChange={(e) => handleTemplateChange(e.target.value)}
          sx={{ mb: 1.5 }}
        >
          {termsTemplates.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </CustomTextField>
      ) : (
        <Typography
          sx={{
            fontSize: "12px",
            color: "text.secondary",
            mb: 1.5,
            p: 1.5,
            bgcolor: colors.primary[800],
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          No terms templates found. Create one in configurations.
        </Typography>
      )}

      <TextField
        multiline
        minRows={4}
        fullWidth
        value={value}
        onChange={handleChange}
        placeholder="Enter terms and conditions..."
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            bgcolor: "background.paper",
            fontSize: "13px",
            color: colors.primary[100],
            lineHeight: 1.8,
            "& fieldset": { border: `1px solid ${colors.primary[800]}` },
            "&:hover fieldset": { borderColor: colors.primary[500] },
            "&.Mui-focused fieldset": {
              borderColor: colors.primary[400],
              borderWidth: 1,
            },
          },
        }}
      />
    </Paper>
  );
}
